import { Product } from '../../../services/api';
import { isTelematics as checkTelematics } from '../../../utils';
import { driverSchema, taxiDriverSchema } from '../../../utils/validation/DriverSchema';
import { driverShortSchema, taxiDriverShortSchema } from '../../../utils/validation/DriverShortSchema';
import { ExtraDriverField } from '../../forms/types';

/**
 * Generates configuration for driver forms based on product type, proposer marital status, and vehicle use.
 * This function isolates the conditional logic required to set up both the full and short driver forms
 * for Renewal and MTA (Mid-Term Adjustment) journeys.
 *
 * @param product - The insurance product type (e.g., private car, taxi).
 * @param proposerMaritalStatus - The marital status of the proposer, used for schema validation.
 * @param vehicleUse - The intended use of the vehicle, affecting schema and extra fields.
 * @returns An object containing:
 *   - `shortFormSchema`: The validation schema for the short driver form.
 *   - `fullFormSchema`: The validation schema for the full driver form.
 *   - `formIntro`: Optional introductory text for the form, e.g., telematics notice.
 *   - `shortFormExtraFields`: Additional fields required for the short form.
 *   - `fullFormExtraFields`: Additional fields required for the full form.
 *   - `motoringClaimsYears`: The claims history period: three years for taxi policies and five years otherwise.
 */
export const getDriverFormConfig = (product: Product, proposerMaritalStatus: string, vehicleUse: string) => {
	const isTaxi = product === Product.TAXI;
	const isTelematics = checkTelematics(product);
	const motoringClaimsYears = isTaxi ? 3 : 5;

	let shortFormSchema = driverShortSchema;
	let fullFormSchema = driverSchema(proposerMaritalStatus);
	let formIntro = '';
	const shortFormExtraFields: ExtraDriverField[] = [];
	const fullFormExtraFields: ExtraDriverField[] = [];

	if (isTelematics) {
		formIntro =
			'Please be aware, as part of the terms and conditions of your policy, any named drivers will also have their driving behaviour monitored.';
	}
	if (isTaxi) {
		fullFormSchema = taxiDriverSchema(proposerMaritalStatus, vehicleUse);
		shortFormSchema = taxiDriverShortSchema(vehicleUse);
		fullFormExtraFields.push('driverUsage', 'badgePassedDate');
		shortFormExtraFields.push('driverUsage');
	}
	return { shortFormSchema, fullFormSchema, formIntro, shortFormExtraFields, fullFormExtraFields, motoringClaimsYears };
};
